import api from './client';
import type { AxiosResponse } from 'axios';

// Notifications
export const getNotifications = (): Promise<AxiosResponse> => api.get('/notifications');
export const markNotificationsRead = (): Promise<AxiosResponse> => api.post('/notifications/read');
export const getUnreadCount = (): Promise<AxiosResponse> => api.get('/notifications/unread-count');

// Events
export const getEvents = (): Promise<AxiosResponse> => api.get('/events');

// Share
export const getShareData = (): Promise<AxiosResponse> => api.get('/share/fair-score');

// Reflection & Date Ideas
export const getReflectionPrompts = (): Promise<AxiosResponse> => api.get('/reflection/prompts');
export const getDateIdeas = (budget?: string): Promise<AxiosResponse> => api.get(`/date-ideas${budget ? `?budget=${budget}` : ''}`);

// Budget Templates
export const getBudgetTemplates = (category?: string): Promise<AxiosResponse> => api.get(`/templates/budget${category ? `?category=${category}` : ''}`);

// Blog
export const getArticles = (category?: string): Promise<AxiosResponse> => api.get(`/blog/articles${category ? `?category=${category}` : ''}`);
export const getArticle = (slug: string): Promise<AxiosResponse> => api.get(`/blog/articles/${slug}`);

// Referral
export const getReferralCode = (): Promise<AxiosResponse> => api.get('/referral/code');
export const applyReferral = (code: string): Promise<AxiosResponse> => api.post(`/referral/apply?code=${encodeURIComponent(code)}`);

// Support
export const getFAQ = (category?: string): Promise<AxiosResponse> => api.get(`/support/faq${category ? `?category=${category}` : ''}`);
export const createTicket = (subject: string, message: string, category: string): Promise<AxiosResponse> => api.post(`/support/tickets?subject=${encodeURIComponent(subject)}&message=${encodeURIComponent(message)}&category=${encodeURIComponent(category)}`);
export const getMyTickets = (): Promise<AxiosResponse> => api.get('/support/tickets');

// Sessions & Tracking
export const startSession = (): Promise<AxiosResponse> => api.post('/sessions/start');
export const endSession = (sessionId: string): Promise<AxiosResponse> => api.post(`/sessions/${sessionId}/end`);
export const sendHeartbeat = (): Promise<AxiosResponse> => api.post('/heartbeat');
export const getUserStatus = (userId: string): Promise<AxiosResponse> => api.get(`/users/${userId}/status`);

// Error logging
export const logError = (data: Record<string, unknown>): Promise<AxiosResponse> => api.post('/error-log', data);

// Onboarding wizard
export const getOnboardingStatus = (): Promise<AxiosResponse> => api.get('/onboarding/status');
export const saveOnboardingStep = (step: number): Promise<AxiosResponse> => api.post('/onboarding/step', { step });
export const completeOnboarding = (): Promise<AxiosResponse> => api.post('/onboarding/complete');

// A/B Testing
export const getExperiments = (): Promise<AxiosResponse> => api.get('/ab/experiments');
export const getExperiment = (id: string): Promise<AxiosResponse> => api.get(`/ab/experiments/${id}`);
export const createExperiment = (data: { name: string; description?: string; variants?: string }): Promise<AxiosResponse> => api.post(`/ab/experiments?name=${encodeURIComponent(data.name)}&description=${encodeURIComponent(data.description || '')}&variants=${encodeURIComponent(data.variants || 'control,variant_a')}`);
export const getABAssignment = (id: string): Promise<AxiosResponse> => api.get(`/ab/assign/${id}`);
export const trackABEvent = (data: { experiment_id: string; event_type?: string }): Promise<AxiosResponse> => api.post(`/ab/events?experiment_id=${data.experiment_id}&event_type=${data.event_type || 'click'}`);

// Gamification
export const getAchievementDefs = (): Promise<AxiosResponse> => api.get('/achievements/definitions');
export const getMyAchievements = (): Promise<AxiosResponse> => api.get('/achievements/me');
export const checkAchievements = (): Promise<AxiosResponse> => api.post('/achievements/check');
export const getLeaderboard = (): Promise<AxiosResponse> => api.get('/achievements/leaderboard');

// Forum
export const getForumCategories = (): Promise<AxiosResponse> => api.get('/forum/categories');
export const getForumThreads = (params: { category_id?: string; page?: number } = {}): Promise<AxiosResponse> => {
  const q = new URLSearchParams();
  if (params.category_id) q.append('category_id', params.category_id);
  if (params.page) q.append('page', String(params.page));
  return api.get(`/forum/threads?${q.toString()}`);
};
export const getForumThread = (id: string): Promise<AxiosResponse> => api.get(`/forum/threads/${id}`);
export const createForumThread = (data: { title: string; body: string; category_id?: string }): Promise<AxiosResponse> => api.post('/forum/threads', { title: data.title, body: data.body, category_id: data.category_id || 'general' });
export const replyToThread = (threadId: string, body: string): Promise<AxiosResponse> => api.post(`/forum/threads/${threadId}/reply`, { body });
export const likeThread = (id: string): Promise<AxiosResponse> => api.post(`/forum/threads/${id}/like`);
